// components/plScenario/table/TemporaryRow.jsx
import React from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';

const TemporaryRow = ({
  tempRow,
  viewMode,
  months,
  quarters,
  editingCell,
  editingTempName,
  currentYear,     
  currentMonth,     
  selectedYear,    
  onNameEdit,
  onNameChange,
  onNameBlur,
  onCellEdit,
  onCellChange,
  onCellBlur,
  onDelete,
  formatCurrency,
}) => {
  const isEditingName = editingTempName === tempRow.id;
  const groupKey = tempRow.groupKey;

  const isEdited = (monthNum) => {
    // Check if user has entered a non-zero value
    const value = tempRow.values[monthNum] || 0;
    
    // Only show as edited if:
    // 1. Value is non-zero, AND
    // 2. Month is in the future
    if (value === 0) return false;
    
    if (selectedYear === currentYear) {
      if (!currentMonth) return false;
      return Number(monthNum) > Number(currentMonth);
    }
    
    return selectedYear > currentYear;
  };

  return (
    <tr className="hover:bg-blue-50 bg-blue-25 border-l-4 border-blue-400">
      <td className="pl-12 py-1.5 text-xs text-blue-700 border-b border-gray-100">
        <div className="flex items-center gap-2">
          {isEditingName ? (
            <input
              type="text"
              value={tempRow.name}
              onChange={(e) => onNameChange(groupKey, tempRow.id, e.target.value)}
              onBlur={() => onNameBlur(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onNameBlur(null);
                if (e.key === 'Escape') {
                  onNameChange(groupKey, tempRow.id, 'New Line Item');
                  onNameBlur(null);
                }
              }}
              autoFocus
              className="flex-1 px-2 py-1 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <>
              <span 
                className="flex-1 cursor-pointer hover:text-blue-900"
                onClick={() => onNameEdit(tempRow.id)}
              >
                {tempRow.name}
              </span>
              <span className="text-[10px] text-blue-600 font-semibold">TEST</span>
            </>
          )}
          <button
            onClick={() => onDelete(groupKey, tempRow.id)}
            className="p-1 hover:bg-red-100 rounded text-red-600"
            title="Delete temporary row"
          >
            <TrashIcon className="w-3 h-3" />
          </button>
        </div>
      </td>
      
      {viewMode === 'monthly' ? (
        months.map((month) => {
          const amount = tempRow.values[month.month_num] || 0;
          const isEditingThisCell =
            editingCell?.accountId === tempRow.id &&
            editingCell?.monthNum === month.month_num;
          const edited = isEdited(month.month_num);
          return (
            <td
              key={month.month_num}
              className={`px-2 py-1.5 text-center text-xs border-b border-gray-100 ${
                month.is_actual
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : edited  // ✅ CHANGE THIS
                  ? 'bg-amber-100 ring-1 ring-amber-400'  // Yellow like regular items
                  : 'bg-blue-50 cursor-pointer hover:bg-blue-100'
              }`}
              onClick={() =>
                !month.is_actual && !isEditingThisCell &&
                onCellEdit(tempRow.id, month.month_num, amount)
              }
            >
              {isEditingThisCell ? (
                <input
                  type="text"
                  value={editingCell.value}
                  onChange={(e) => onCellChange(e.target.value)}
                  onBlur={() => onCellBlur(groupKey, tempRow.id, editingCell.monthNum, editingCell.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onCellBlur(groupKey, tempRow.id, editingCell.monthNum, editingCell.value);
                    }
                    if (e.key === 'Escape') onCellBlur(null);
                  }}
                  autoFocus
                  className="w-full px-1 text-center border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
                />
              ) : (
                <div className="flex items-center justify-center">
                  {formatCurrency(amount)}
                  {/* ✅ ADD: Show checkmark if edited */}
                  {edited && amount !== 0 && (
                    <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-amber-700 bg-amber-200 rounded-full border border-amber-400">
                      ✓
                    </span>
                  )}
                </div>
              )}
            </td>
          );
        })
      ) : (
        quarters.map((quarter) => {
          const quarterTotal = quarter.months.reduce((sum, month) => {
            return sum + (tempRow.values[month.month_num] || 0);
          }, 0);

          return (
            <td
              key={quarter.quarter}
              className={`px-2 py-1.5 text-right text-xs border-b border-gray-100 ${
                quarter.is_actual
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-50 cursor-pointer hover:bg-blue-100'
              }`}
            >
              {formatCurrency(quarterTotal)}
            </td>
          );
        })
      )}
      
      <td className="px-4 py-3 text-center text-xs bg-blue-50 border-b border-gray-100">
        {formatCurrency(
          Object.values(tempRow.values).reduce((s, v) => s + v, 0)
        )}
      </td>
    </tr>
  );
};

export default TemporaryRow;